import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_MAX_PROGRESS_OFFSET,
  PullToRefreshLoader,
} from '@/components/pull-to-refresh-loader';
import { FilterOption, OrderHistoryFilters } from './order-history-filters';
import { LIST_HEIGHT } from '@/components/horizontal-selector';
import { OfflineComponent } from '@/components/offline-component';
import { OrderHistoryList } from './order-history-list';
import { OrdersEmptyState } from './orders-empty-state';
import { RefreshControl, View } from 'react-native';
import { SCREEN_PADDING } from '@/styles';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useGetCreditRequestBatches } from '@/hooks/use-get-credit-request-batches';
import { useGetInvoicesWithoutOrders } from '@/hooks/use-get-invoices-without-orders';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOrderHistoryListQuery } from '@/hooks/use-order-history-list-query';
import { usePaymentsContext } from '@/contexts/payments-context';
import { usePullToRefreshState } from '@/components/use-pull-to-refresh-state';
import { useUserContext } from '@/contexts/user-context';

export interface Props {
  onCreditRequestSelect: (creditRequestUUID: string) => void;
  onInvoiceSelect: (invoiceUUID: string) => void;
  onNewOrderPress: () => void;
  onOrderSelect: (orderUUID: string) => void;
  showPastOrders: boolean;
}

export const OrderHistoryTab: React.FC<Props> = ({
  onCreditRequestSelect,
  onInvoiceSelect,
  onNewOrderPress,
  onOrderSelect,
  showPastOrders,
}) => {
  const [styles] = useStyles();
  const isOfflineModeActive = useOfflineModeActive();

  const [offset, setOffset] = useState(0);
  const [isScrollDragging, setIsScrollDragging] = useState(false);

  const {
    fetchingMore: fetchingMoreOrders,
    fetchMore: fetchMoreOrders,
    loading: loadingOrders,
    hasResponse: hasOrdersResponse,
    orders,
    refresh: refreshOrders,
    refreshing: refreshingOrders,
  } = useOrderHistoryListQuery(showPastOrders);

  const {
    fetchMore: fetchMoreInvoices,
    fetchingMore: fetchingMoreInvoices,
    loading: loadingInvoices,
    hasResponse: hasInvoicesResponse,
    refresh: refreshInvoices,
    refreshing: refreshingInvoices,
    pastInvoices,
    upcomingInvoices,
  } = useGetInvoicesWithoutOrders();

  const {
    creditRequests,
    fetchMore: fetchMoreCreditRequests,
    fetchingMore: fetchingMoreCreditRequests,
    loading: loadingCreditRequests,
    hasResponse: hasCreditRequestsResponse,
    refresh: refreshCreditRequests,
    refreshing: refreshingCreditRequests,
  } = useGetCreditRequestBatches();

  const isRefreshing =
    refreshingInvoices || refreshingOrders || refreshingCreditRequests;

  const { isEnabled, showDots } = usePullToRefreshState({
    isRefreshing,
    isScrollDragging,
  });

  const {
    selectedRestaurantRelationship: { consumptionTax, displayItemPrices },
  } = useUserContext();
  const { useV2Invoices } = usePaymentsContext();

  const [selectedOrderHistoryFilter, setSelectedOrderHistoryFilter] = useState(
    FilterOption.Pepper,
  );

  // Simplified upcomingDataSection to handle both Pepper and External channels
  const upcomingDataSection = useMemo(() => {
    const upcomingOrders = showPastOrders ? [] : orders;
    const ordersData =
      selectedOrderHistoryFilter === FilterOption.Pepper
        ? upcomingOrders
        : upcomingInvoices;
    return [
      {
        data: ordersData,
        key: null,
      },
    ];
  }, [showPastOrders, orders, selectedOrderHistoryFilter, upcomingInvoices]);

  const pastDataSection = useMemo(() => {
    switch (selectedOrderHistoryFilter) {
      case FilterOption.CreditRequests:
        return [
          {
            data: creditRequests,
            key: null,
          },
        ];
      case FilterOption.External:
        return [
          {
            data: pastInvoices,
            key: null,
          },
        ];
      default:
        return [
          {
            data: showPastOrders ? orders : [],
            key: null,
          },
        ];
    }
  }, [
    showPastOrders,
    orders,
    selectedOrderHistoryFilter,
    pastInvoices,
    creditRequests,
  ]);

  useComponentFocus({
    onFocus: useCallback(() => {
      refreshInvoices();
      refreshOrders();
      refreshCreditRequests();
    }, [refreshCreditRequests, refreshInvoices, refreshOrders]),
  });

  // Check if there are any orders to display
  const hasOrders = showPastOrders
    ? Boolean(pastDataSection[0].data.length)
    : Boolean(upcomingDataSection[0].data.length);

  const hasCreditRequests = creditRequests.length > 0 && showPastOrders;
  const hasExternalOrders =
    useV2Invoices && (pastInvoices.length > 0 || upcomingInvoices.length > 0);
  const hasPepperOrders = useV2Invoices && orders.length > 0;

  const availableSourcesCount = [
    hasPepperOrders,
    hasExternalOrders,
    hasCreditRequests,
  ].filter(Boolean).length;

  const showOrderHistoryFilters = availableSourcesCount > 1;

  // Update the selectedOrderChannel based on data availability
  useEffect(() => {
    // Read raw orders, not the filter-gated section data, so returning orders
    // can re-select Pepper instead of latching on an empty channel.
    const hasPepperData = orders.length > 0;
    const hasExternalData =
      (showPastOrders ? pastInvoices : upcomingInvoices).length > 0;
    const hasCreditRequestsData = creditRequests.length > 0 && showPastOrders;

    if (hasPepperData && !hasExternalData && !hasCreditRequestsData) {
      setSelectedOrderHistoryFilter(FilterOption.Pepper);
    } else if (!hasPepperData && !hasCreditRequestsData && hasExternalData) {
      setSelectedOrderHistoryFilter(FilterOption.External);
    } else if (!hasPepperData && !hasExternalData && hasCreditRequestsData) {
      setSelectedOrderHistoryFilter(FilterOption.CreditRequests);
    }
  }, [
    showPastOrders,
    orders,
    pastInvoices,
    upcomingInvoices,
    creditRequests.length,
  ]);

  if (isOfflineModeActive) {
    return <OfflineComponent />;
  }

  if (
    (loadingInvoices || loadingOrders || loadingCreditRequests) &&
    !fetchingMoreInvoices &&
    !fetchingMoreOrders &&
    !fetchingMoreCreditRequests &&
    !hasInvoicesResponse &&
    !hasOrdersResponse &&
    !hasCreditRequestsResponse &&
    !refreshingInvoices &&
    !refreshingOrders &&
    !refreshingCreditRequests
  ) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator testID="loading-indicator" />
      </View>
    );
  }

  return hasOrders ? (
    <>
      {showOrderHistoryFilters ? (
        <View style={styles.orderChannelSelectorContainer}>
          <OrderHistoryFilters
            hasCreditRequests={hasCreditRequests}
            hasExternalOrders={hasExternalOrders}
            hasPepperOrders={hasPepperOrders}
            onFilterSelect={setSelectedOrderHistoryFilter}
            selectedFilter={selectedOrderHistoryFilter}
          />
        </View>
      ) : null}
      <PullToRefreshLoader
        containerStyle={
          showPastOrders ? styles.loaderWithTopPadding : undefined
        }
        height={Math.max(-offset, 0)}
        isEnabled={isEnabled}
        isScrollDragging={isScrollDragging}
        progress={Math.min(
          (Math.max(-offset, 0) / DEFAULT_MAX_PROGRESS_OFFSET) * 100,
          100,
        )}
        showDots={showDots}
      />

      <OrderHistoryList
        consumptionTax={consumptionTax}
        contentContainerStyle={
          !showOrderHistoryFilters
            ? styles.withoutOrderChannelAndRequestsSelector
            : undefined
        }
        displayItemPrices={displayItemPrices}
        ListFooterComponent={
          fetchingMoreInvoices || fetchingMoreOrders ? (
            <ActivityIndicator />
          ) : null
        }
        onCreditRequestSelect={onCreditRequestSelect}
        onEndReached={() => {
          if (selectedOrderHistoryFilter === FilterOption.Pepper) {
            fetchMoreOrders();
          } else if (selectedOrderHistoryFilter === FilterOption.External) {
            fetchMoreInvoices();
          } else if (
            selectedOrderHistoryFilter === FilterOption.CreditRequests
          ) {
            fetchMoreCreditRequests();
          }
        }}
        onInvoiceSelect={onInvoiceSelect}
        onOrderSelect={onOrderSelect}
        // -112 is the max before refresh happens
        onScroll={(e) => {
          setOffset(e.nativeEvent?.contentOffset?.y ?? 0);
        }}
        onScrollBeginDrag={() => {
          setIsScrollDragging(true);
        }}
        onScrollEndDrag={() => {
          setIsScrollDragging(false);
          setOffset(0);
        }}
        refreshControl={
          <RefreshControl
            colors={['transparent']}
            onRefresh={() => {
              refreshInvoices(true);
              refreshOrders(true);
              refreshCreditRequests(true);
            }}
            // all properties must be transparent to hide the original loader
            refreshing={
              refreshingInvoices || refreshingOrders || refreshingCreditRequests
            }
            style={styles.nativeRefreshControl}
            tintColor="transparent"
          />
        }
        sections={showPastOrders ? pastDataSection : upcomingDataSection}
        testID={
          showPastOrders
            ? selectedOrderHistoryFilter === FilterOption.CreditRequests &&
              creditRequests.length
              ? 'credit-requests-list'
              : selectedOrderHistoryFilter === FilterOption.Pepper &&
                  orders.length
                ? 'past-orders-list'
                : 'external-orders-list'
            : 'upcoming-orders-list'
        }
      />
    </>
  ) : (
    <>
      {showOrderHistoryFilters ? (
        <View style={styles.orderChannelSelectorContainer}>
          <OrderHistoryFilters
            hasCreditRequests={hasCreditRequests}
            hasExternalOrders={hasExternalOrders}
            hasPepperOrders={hasPepperOrders}
            onFilterSelect={setSelectedOrderHistoryFilter}
            selectedFilter={selectedOrderHistoryFilter}
          />
        </View>
      ) : null}
      <OrdersEmptyState onNewOrderPress={onNewOrderPress} />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  loaderWithTopPadding: {
    top: 26,
  },
  loadingContainer: {
    marginVertical: SCREEN_PADDING,
  },
  nativeRefreshControl: {
    backgroundColor: 'transparent',
  },
  orderChannelSelectorContainer: {
    backgroundColor: theme.colors.transparent,
    height: LIST_HEIGHT + 16,
    paddingTop: 12,
  },
  withoutOrderChannelAndRequestsSelector: {
    paddingTop: 8,
  },
}));
